import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getCompanySettings } from "@/hooks/useCompanySettings";
import { jobCardsApi } from "@/services/api";

interface JobCard {
  id: number;
  jobNo: string;
  title: string;
  client: string;
  clientContact: string;
  location: string;
  services: string[];
  status: string;
  assignedDate: string;
  createdDate: string;
  dueDate: string;
  fieldTeam: string[];
  officeTeam: string[];
  progress: number;
  priority: string;
  area: string;
  remarks: string;
}

/** Map DB snake_case → frontend camelCase (same logic as detail page) */
function normalizeJobCard(raw: Record<string, unknown>): JobCard {
  const rawServices = (raw.services as Array<Record<string, unknown> | string>) ?? [];
  const services = rawServices.map((s) =>
    typeof s === "string" ? s : String((s as Record<string, unknown>).service_name ?? "")
  );

  const rawTeam = (raw.team as Array<Record<string, unknown>>) ?? [];
  const fieldTeam = rawTeam
    .filter((t) => t.team_type === "field")
    .map((t) => String(t.employee_name ?? ""));
  const officeTeam = rawTeam
    .filter((t) => t.team_type === "office")
    .map((t) => String(t.employee_name ?? ""));

  return {
    id: Number(raw.id),
    jobNo: String(raw.job_no ?? raw.jobNo ?? ""),
    title: String(raw.title ?? ""),
    client: String(raw.client_name ?? raw.client ?? ""),
    clientContact: String(raw.contact_person ?? raw.clientContact ?? ""),
    location: String(raw.location ?? ""),
    services,
    status: String(raw.status ?? "Pending"),
    assignedDate: String(raw.assigned_date ?? raw.assignedDate ?? ""),
    createdDate: String(raw.created_at ?? raw.createdDate ?? ""),
    dueDate: String(raw.due_date ?? raw.dueDate ?? ""),
    fieldTeam,
    officeTeam,
    progress: Number(raw.progress ?? 0),
    priority: String(raw.priority ?? "Medium"),
    area: String(raw.area ?? ""),
    remarks: String(raw.remarks ?? ""),
  };
}

export default function PrintJobCardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobCard | null>(null);
  const [loading, setLoading] = useState(true);
  const company = getCompanySettings();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const raw = await jobCardsApi.getById(Number(id));
        setJob(normalizeJobCard(raw as Record<string, unknown>));
      } catch {
        setJob(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading job card...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Job Card not found</p>
          <button onClick={() => navigate("/job-cards")} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm cursor-pointer">
            Back to Job Cards
          </button>
        </div>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    "Pending": "#d97706",
    "In Progress": "#2563eb",
    "Completed": "#16a34a",
    "Delivered": "#7c3aed",
  };
  const statusColor = statusColors[job.status] || "#6b7280";

  return (
    <>
      {/* Screen toolbar — hidden on print */}
      <div className="print:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/job-cards")}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm" /></span>
            Back
          </button>
          <span className="text-sm font-semibold text-gray-700">Job Card: {job.jobNo}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line text-sm" /></span>
            Print / Save PDF
          </button>
        </div>
      </div>

      {/* A4 Page */}
      <div className="print:pt-0 pt-16 min-h-screen bg-gray-100 print:bg-white flex justify-center">
        <div
          className="bg-white w-full print:w-full print:shadow-none my-6 print:my-0"
          style={{ maxWidth: "210mm", minHeight: "297mm", padding: "20mm" }}
          id="print-area"
        >
          {/* ── HEADER ── */}
          <div style={{ borderBottom: "3px solid #10b981", paddingBottom: "16px", marginBottom: "20px" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {company.logo && (
                  <img src={company.logo} alt="logo" style={{ width: "52px", height: "52px", objectFit: "contain" }} />
                )}
                <div>
                  <h1 style={{ fontSize: "16px", fontWeight: "800", color: "#111827", margin: 0 }}>{company.name}</h1>
                  <p style={{ fontSize: "10px", color: "#6b7280", margin: "2px 0 0" }}>{company.address}</p>
                  <p style={{ fontSize: "10px", color: "#6b7280", margin: "1px 0 0" }}>
                    {company.email} &nbsp;|&nbsp; {company.phone}
                  </p>
                  {company.gstin && (
                    <p style={{ fontSize: "10px", color: "#6b7280", margin: "1px 0 0" }}>GSTIN: {company.gstin}</p>
                  )}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ fontSize: "10px", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", margin: 0 }}>Job Card</p>
                <p style={{ fontSize: "20px", fontWeight: "800", color: "#10b981", fontFamily: "monospace", margin: "2px 0" }}>{job.jobNo}</p>
                <p style={{ fontSize: "10px", color: "#6b7280", margin: "2px 0" }}>Created: {job.createdDate}</p>
                <span style={{ fontSize: "10px", fontWeight: "700", padding: "2px 8px", borderRadius: "999px", background: `${statusColor}15`, color: statusColor }}>
                  {job.status}
                </span>
              </div>
            </div>
          </div>

          {/* ── CLIENT + PROJECT ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
            <div style={{ padding: "12px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #f3f4f6" }}>
              <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Client Details</p>
              <p style={{ fontSize: "13px", fontWeight: "700", color: "#111827", margin: "0 0 3px" }}>{job.clientContact}</p>
              <p style={{ fontSize: "11px", color: "#374151", margin: "0 0 2px" }}>{job.client}</p>
              <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 2px" }}>Location: {job.location}</p>
            </div>
            <div style={{ padding: "12px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #f3f4f6" }}>
              <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Project Details</p>
              <p style={{ fontSize: "12px", fontWeight: "700", color: "#111827", margin: "0 0 3px" }}>{job.title}</p>
              <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 2px" }}>Survey Area: {job.area}</p>
              <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 2px" }}>Assigned: {job.assignedDate} &nbsp;|&nbsp; Due: {job.dueDate}</p>
              <p style={{ fontSize: "10px", color: "#6b7280", margin: "0" }}>Priority: <strong style={{ color: "#111827" }}>{job.priority}</strong></p>
            </div>
          </div>

          {/* ── SERVICES ── */}
          <div style={{ marginBottom: "16px" }}>
            <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Services</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {job.services.map((s) => (
                <span key={s} style={{ fontSize: "10px", fontWeight: "600", padding: "3px 10px", borderRadius: "999px", background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" }}>
                  {s}
                </span>
              ))}
            </div>
          </div>

          {/* ── SCOPE / REMARKS ── */}
          {job.remarks && (
            <div style={{ marginBottom: "16px", padding: "12px", background: "#fffbeb", borderRadius: "8px", border: "1px solid #fde68a" }}>
              <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Site Conditions / Remarks</p>
              <p style={{ fontSize: "11px", color: "#374151", lineHeight: "1.6", margin: 0 }}>{job.remarks}</p>
            </div>
          )}

          {/* ── TEAM ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
            <div>
              <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Field Team</p>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ fontSize: "9px", padding: "4px 8px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Name</th>
                    <th style={{ fontSize: "9px", padding: "4px 8px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {job.fieldTeam.map((m, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ fontSize: "10px", padding: "5px 8px", color: "#111827", fontWeight: "500" }}>{m}</td>
                      <td style={{ fontSize: "10px", padding: "5px 8px", color: "#6b7280" }}>Field Surveyor</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>Office Team (CAD/GIS)</p>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    <th style={{ fontSize: "9px", padding: "4px 8px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Name</th>
                    <th style={{ fontSize: "9px", padding: "4px 8px", textAlign: "left", color: "#6b7280", fontWeight: "600" }}>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {job.officeTeam.map((m, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ fontSize: "10px", padding: "5px 8px", color: "#111827", fontWeight: "500" }}>{m}</td>
                      <td style={{ fontSize: "10px", padding: "5px 8px", color: "#6b7280" }}>CAD/GIS Engineer</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── STATUS TIMELINE ── */}
          <div style={{ marginBottom: "20px" }}>
            <p style={{ fontSize: "9px", fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "10px" }}>Status Timeline</p>
            <div style={{ display: "flex", gap: "0" }}>
              {["Pending", "In Progress", "Completed", "Delivered"].map((s, i) => {
                const statuses = ["Pending", "In Progress", "Completed", "Delivered"];
                const currentIdx = statuses.indexOf(job.status);
                const isDone = i <= currentIdx;
                return (
                  <div key={s} style={{ flex: 1, textAlign: "center", position: "relative" }}>
                    <div style={{
                      width: "28px", height: "28px", borderRadius: "50%", margin: "0 auto 6px",
                      background: isDone ? "#10b981" : "#e5e7eb",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "12px", color: isDone ? "#fff" : "#9ca3af", fontWeight: "700",
                      position: "relative", zIndex: 1,
                    }}>
                      {isDone ? "✓" : i + 1}
                    </div>
                    {i < 3 && (
                      <div style={{
                        position: "absolute", top: "14px", left: "50%", right: "-50%", height: "2px",
                        background: i < currentIdx ? "#10b981" : "#e5e7eb", zIndex: 0,
                      }} />
                    )}
                    <p style={{ fontSize: "9px", color: isDone ? "#065f46" : "#9ca3af", fontWeight: isDone ? "700" : "400", margin: 0 }}>{s}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── PROGRESS ── */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
              <p style={{ fontSize: "10px", color: "#6b7280", margin: 0 }}>Overall Progress</p>
              <p style={{ fontSize: "10px", fontWeight: "700", color: "#111827", margin: 0 }}>{job.progress}%</p>
            </div>
            <div style={{ height: "6px", background: "#e5e7eb", borderRadius: "999px", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${job.progress}%`, background: "#10b981", borderRadius: "999px" }} />
            </div>
          </div>

          {/* ── DECLARATION ── */}
          <div style={{ marginBottom: "24px", padding: "10px 12px", background: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0" }}>
            <p style={{ fontSize: "10px", color: "#065f46", margin: 0, lineHeight: "1.6" }}>
              This Job Card authorizes the assigned team to carry out the survey work as described above. All field operations must comply with company SOPs and applicable regulations. Any changes to scope must be approved by the Survey Manager.
            </p>
          </div>

          {/* ── SIGNATURES ── */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", marginTop: "32px" }}>
            {[
              { role: "Field Surveyor", name: job.fieldTeam[0] || "_______________" },
              { role: "Survey Manager", name: "Priya Sharma" },
              { role: "Client Representative", name: job.clientContact || "_______________" },
            ].map((sig) => (
              <div key={sig.role} style={{ textAlign: "center" }}>
                <div style={{ height: "48px", borderBottom: "1.5px solid #374151", marginBottom: "6px" }} />
                <p style={{ fontSize: "10px", fontWeight: "700", color: "#111827", margin: "0 0 2px" }}>{sig.name}</p>
                <p style={{ fontSize: "9px", color: "#6b7280", margin: "0 0 2px" }}>{sig.role}</p>
                <p style={{ fontSize: "9px", color: "#9ca3af", margin: 0 }}>Date: ___________</p>
              </div>
            ))}
          </div>

          {/* ── FOOTER ── */}
          <div style={{ marginTop: "24px", paddingTop: "12px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p style={{ fontSize: "8px", color: "#9ca3af", margin: 0 }}>
              This is a computer-generated document. | {company.name}
            </p>
            <p style={{ fontSize: "8px", color: "#9ca3af", margin: 0 }}>
              {job.jobNo} | Page 1 of 1
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
